import { createFeatureSelector, createSelector, MemoizedSelector, select } from '@ngrx/store';
import { ScheduleEditorProgress } from 'app/admin/schedules/schedules-editor/incremental-progress-schema';
import { first, map, merge, Observable, OperatorFunction, pipe, switchMap, throwError } from 'rxjs';
import { pickTruthy } from 'Util';
import * as fromScheduleEditor from './schedule-editor.reducer';

/**
 * Creates an rxjs pipe that only emits truthy values from the given selector.
 */
const selectTruthy = <T, K>(selector: (state: T) => K) => pipe(select(selector), pickTruthy());

/**
 * Creates an rxjs pipe that mirrors `successPipe`, and emits errors from `errorPipe`.
 */
const mergeWithError =
	<Input, Output, Error>(successPipe: OperatorFunction<Input, Output>, errorPipe: OperatorFunction<Input, Error>) =>
	(state: Observable<Input>) =>
		merge(
			state.pipe(successPipe),
			state.pipe(
				errorPipe,
				switchMap((error) => throwError(() => error))
			)
		);

/**
 * Creates an rxjs pipe that emits the loaded value for the given id, or an error.
 */
const statusStateSelector = (operationStateById: MemoizedSelector<object, fromScheduleEditor.IncrementalProgressState['create'][string]>) => {
	const getOperationResult = createSelector(operationStateById, loadedIncrementalProgressesMap, (opState, progresses) =>
		opState?.status === 'loaded' ? progresses?.[opState.id] : undefined
	);

	const getOperationError = createSelector(operationStateById, (opState) => (opState?.status === 'error' ? opState.error : undefined));

	return pipe(mergeWithError(selectTruthy(getOperationResult), selectTruthy(getOperationError)), first());
};

const scheduleEditorState = createFeatureSelector<fromScheduleEditor.State>(fromScheduleEditor.schedulesEditorFeatureKey);

const incrementalProgressState = createSelector(scheduleEditorState, (editor) => editor.incrementalProgress);
const loadedState = createSelector(incrementalProgressState, (state) => (state.status === 'loaded' ? state : undefined));

export const loadedIncrementalProgressesAt = createSelector(loadedState, (state) => state?.loadedAt);

const loadedIncrementalProgressesMap = createSelector(loadedState, (state) => state?.byId);

const incrementalProgressError = createSelector(incrementalProgressState, (state) => (state.status === 'error' ? state.error : undefined));

/**
 * Emits a list of loaded progresses or fails with an error if loaded progresses is in an error state.
 */
export const selectLoadedProgresses = pipe(
	mergeWithError(selectTruthy(loadedIncrementalProgressesMap), selectTruthy(incrementalProgressError)),
	map((progresses) => Object.values(progresses))
);

const loadedIncrementalProgress = (id: ScheduleEditorProgress['id']) =>
	createSelector(loadedIncrementalProgressesMap, (progresses) => progresses?.[id]);
/**
 * Emits a value for the given id. Never emits an error.
 */
export const selectLoadedIncrementalProgress = (id: ScheduleEditorProgress['id']) => selectTruthy(loadedIncrementalProgress(id));

/**
 * Emits a value for the created progress with the given correlationId and completes or fails with an error if it is in an error state.
 * This mirrors the behavior of an HTTP request.
 */
export const selectCreatedProgress = (correlationId: string) =>
	statusStateSelector(createSelector(incrementalProgressState, (progress) => progress.create[correlationId]));

/**
 * Emits a value for the updated progress with the given correlationId and completes or fails with an error if it is in an error state.
 * This mirrors the behavior of an HTTP request.
 */
export const selectUpdatedProgress = (correlationId: string) =>
	statusStateSelector(createSelector(incrementalProgressState, (progress) => progress.update[correlationId]));
